import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useData } from '../contexts/DataContext';
import { useTranslation } from 'react-i18next';
// Import icons
import { FaPhone, FaWhatsapp, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { BsFacebook, BsInstagram, BsLinkedin } from 'react-icons/bs';
import { FaXTwitter } from "react-icons/fa6";
import { BiBuilding, BiTime } from 'react-icons/bi';
import { MdEmail, MdLocationOn, MdConstruction } from 'react-icons/md';
import SocialIcons from '../components/Common/SocialIcons';
import PageHero from '../components/PageHero';



const Contact = () => {
  const { t } = useTranslation();
  const result = useData();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { language, updateDocumentTitle } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    company: ''
  });
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const { site_info, navigation, contact_info, social_media } = result.data;
  updateDocumentTitle((t('footer.contact')), site_info);

  useEffect(() => {
    if (result.data) {
      setLoading(false);
    }
  }, [result.data]);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required');
    } else if (formData.name.trim().length < 3) {
      newErrors.name = t('validation.name_min_length');
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = t('validation.required');
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = t('validation.email_invalid');
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = t('validation.required');
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = t('validation.required');
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = t('validation.required');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('validation.message_min_length');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitStatus({
        type: 'error',
        message: t('contact.form.validation_error')
      });
      return;
    }

    setSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: t('contact.form.success_message')
        });
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          company: ''
        });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.message || t('contact.form.error_message')
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: t('contact.form.error_message')
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-stone-50">
        <div className="space-y-6 w-full max-w-md animate-pulse">
          <div className="h-8 rounded bg-stone-200"></div>
          <div className="h-32 rounded bg-stone-200"></div>
          <div className="h-8 rounded bg-stone-200"></div>
        </div>
      </div>
    );
  }

  const renderSocialIcons = () => (
    <SocialIcons socialMedia={social_media} translationFunc={t} translationKeyPrefix="contact.social" />
  );

  return (
    <div className="relative">
      {loading && (
        <div className="flex absolute inset-0 z-50 justify-center items-center bg-white bg-opacity-75">
          <div className="w-12 h-12 rounded-full border-t-2 border-b-2 animate-spin border-stone-800"></div>
        </div>
      )}
      <section className="py-0 bg-center bg-no-repeat bg-cover">
        <PageHero type="contact" />
        <div className="container px-4 mx-auto">
          <div className="container px-4 mx-auto">
            <div className="mx-auto max-w-6xl">
              <div className="grid grid-cols-1 gap-1 lg:gap-12 lg:grid-cols-3">
                {/* Contact Information */}
                <div className="lg:col-span-1">
                  <div className="p-8 bg-white rounded-2xl border shadow-xl border-stone-100">
                    <h3 className="mb-8 text-2xl font-bold text-stone-800">
                      {t('contact.connectWithUs')}
                    </h3>
                    {/* Contact Methods */}
                    <div className="space-y-6">
                      {/* Phone */}
                      <a href={`tel:${contact_info?.phone?.replace(/\s+/g, '')}`} title={t('contact.phone')}
                        className="flex gap-4 items-center p-1 rounded-xl transition-all duration-300 hover:bg-stone-50">
                        <div className="p-3 rounded-full transition-colors duration-300 bg-stone-100 hover:bg-blue-100">
                          <FaPhone className="w-6 h-6 text-stone-600 hover:text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-stone-800">{t('contact.callUs')}</div>
                          <div className="text-stone-600" dir="ltr">{contact_info.phone}</div>
                        </div>
                      </a>

                      {/* Email */}
                      <a href={`mailto:${contact_info.email}`} title={t('contact.email')}
                        className="flex gap-4 items-center p-1 rounded-xl transition-all duration-300 hover:bg-stone-50">
                        <div className="p-3 rounded-full transition-colors duration-300 bg-stone-100 hover:bg-blue-100">
                          <MdEmail className="w-6 h-6 text-stone-600 hover:text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-stone-800">{t('contact.emailUs')}</div>
                          <div className="text-stone-600">{contact_info.email}</div>
                        </div>
                      </a>

                      {/* Location */}
                      <div className="flex gap-4 items-center p-1 rounded-xl">
                        <div className="p-3 rounded-full transition-colors duration-300 bg-stone-100 hover:bg-blue-100">
                          <MdLocationOn className="w-6 h-6 text-stone-600 hover:text-blue-600" />
                        </div>
                        <div>
                          <div className="font-semibold text-stone-800">{t('projects.location')}</div>
                          <div className="text-stone-600">{contact_info.address}</div>
                        </div>
                      </div>

                      {/* Social Media Links */}
                      <div className="pt-6 border-t border-stone-100">
                        {renderSocialIcons()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Form */}
                <div className="lg:col-span-2">
                  <div className="p-8 bg-white rounded-2xl border shadow-xl border-stone-100">
                    <h3 className="flex items-center mb-8 text-2xl font-bold text-stone-800">
                      <BiBuilding className="ml-3 text-3xl text-stone-600" />
                      {t('contact.form.request_type')}
                    </h3>

                    <form onSubmit={handleSubmit} className="space-y-3">
                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <label className="block mb-2 text-sm font-medium text-stone-700">
                            {t('contact.form.full_name')} *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className={`px-3 py-2 w-full rounded-lg border transition-colors duration-300 ${errors.name ? 'border-red-500 bg-red-50' : 'border-stone-200'
                              }`}
                            required
                          />
                          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                        </div>

                        <div>
                          <label className="block mb-2 text-sm font-medium text-stone-700">
                            {t('contact.form.phone')} *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className={`px-3 py-2 w-full rounded-lg border transition-colors duration-300 ${errors.phone ? 'border-red-500 bg-red-50' : 'border-stone-200'
                              }`}
                            required
                          />
                          {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                        </div>
                      </div>

                      <div className="grid gap-6 md:grid-cols-2">
                        <div>
                          <label className="block mb-2 text-sm font-medium text-stone-700">
                            {t('contact.form.email')} *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={`px-3 py-2 w-full rounded-lg border transition-colors duration-300 ${errors.email ? 'border-red-500 bg-red-50' : 'border-stone-200'
                              }`}
                            required
                          />
                          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                        </div>

                        <div>
                          <label className="block mb-2 text-sm font-medium text-stone-700">
                            {t('contact.form.company')}
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            className="px-3 py-2 w-full rounded-lg border transition-colors duration-300 border-stone-200"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-stone-700">
                          {t('contact.form.subject')} *
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className={`px-3 py-2 w-full rounded-lg border transition-colors duration-300 ${errors.subject ? 'border-red-500 bg-red-50' : 'border-stone-200'
                            }`}
                          required
                        />
                        {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-medium text-stone-700">
                          {t('contact.form.message')} *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleInputChange}
                          rows="5"
                          className={`px-3 py-2 w-full rounded-lg border transition-colors duration-300 ${errors.message ? 'border-red-500 bg-red-50' : 'border-stone-200'
                            }`}
                          placeholder={t('contact.form.message_placeholder')}
                          required
                        ></textarea>
                        {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
                      </div>

                      <button
                        type="submit"
                        className="flex justify-center items-center px-6 py-3 w-full text-white rounded-lg transition-colors duration-300 bg-stone-800 hover:bg-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-800 focus:ring-offset-2 disabled:opacity-50"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <span className="inline-flex items-center">
                            <div className="w-5 h-5 mr-3 border-t-2 border-r-2 border-white rounded-full animate-spin"></div>
                            {t('contact.form.submitting')}
                          </span>
                        ) : (
                          <>
                            <MdConstruction className="mr-2" />
                            {t('contact.form.submit')}
                          </>
                        )}
                      </button>
                    </form>

                    {submitStatus.message && (
                      <div className={`mt-4 p-4 rounded-lg ${submitStatus.type === 'success'
                        ? 'bg-green-50 text-green-800'
                        : 'bg-red-50 text-red-800'
                        }`}>
                        {submitStatus.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;